import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ScheduledTaskDto, ScheduledTaskExecutionDto } from '@cpm/shared';
import { api } from '../api/client.js';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorNote, Input, Label, Spinner, statusTone } from './ui.js';

export function ScheduledTasksPanel({ appId, enabled, customer = false }: { appId: string; enabled: boolean; customer?: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [timeout, setTimeoutValue] = useState(300);
  const [name, setName] = useState('');
  const [command, setCommand] = useState('');
  const [frequency, setFrequency] = useState('* * * * *');
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const key = ['applications', appId, 'scheduled-tasks'];

  const tasks = useQuery({
    queryKey: key,
    queryFn: () =>
      api.get<ScheduledTaskDto[]>(`/api/applications/${appId}/scheduled-tasks`),
    enabled,
  });
  const executions = useQuery({
    queryKey: [...key, selectedTask, 'executions'],
    queryFn: () =>
      api.get<ScheduledTaskExecutionDto[]>(
        `/api/applications/${appId}/scheduled-tasks/${selectedTask}/executions?page=1`,
      ),
    enabled: selectedTask !== null,
  });
  const create = useMutation({
    mutationFn: () =>
      editing ? api.patch(`/api/applications/${appId}/scheduled-tasks/${editing}`, { name, command, frequency, timeout }) : api.post(`/api/applications/${appId}/scheduled-tasks`, {
        name,
        command,
        frequency,
        timeout,
        enabled: false,
      }),
    onSuccess: () => {
      setEditing(null);
      setTimeoutValue(300);
      setName('');
      setCommand('');
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const toggle = useMutation({
    mutationFn: (task: ScheduledTaskDto) =>
      api.patch(`/api/applications/${appId}/scheduled-tasks/${task.uuid}`, {
        enabled: !task.enabled,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
  const remove = useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/api/applications/${appId}/scheduled-tasks/${uuid}`),
    onSuccess: () => { setSelectedTask(null); void queryClient.invalidateQueries({ queryKey: key }); },
  });

  if (!enabled) return <p>Scheduled tasks are unavailable for this application.</p>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Scheduled tasks"
          description="Commands run inside this application. Create tasks disabled, review their commands and schedule, then enable them. Store secrets in environment variables, not commands."
        />
        <form
          className="grid gap-3 px-5 py-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <div>
            <Label htmlFor="task-name">Name</Label>
            <Input
              id="task-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="task-command">Command</Label>
            <Input
              id="task-command"
              required
              value={command}
              onChange={(event) => setCommand(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="task-frequency">Cron frequency</Label>
            <Input
              id="task-frequency"
              required
              value={frequency}
              onChange={(event) => setFrequency(event.target.value)}
            />
          </div>
          <div><Label htmlFor="task-timeout">Timeout (seconds)</Label><Input id="task-timeout" type="number" min={1} max={86400} value={timeout} onChange={(e) => setTimeoutValue(Number(e.target.value))} /></div>
          <div className="flex items-end gap-2">
            <Button type="submit" loading={create.isPending}>
              {editing ? 'Save task' : 'Add disabled'}
            </Button>
            {editing && <Button type="button" variant="secondary" onClick={() => { setEditing(null); setName(''); setCommand(''); setFrequency('* * * * *'); setTimeoutValue(300); }}>Cancel edit</Button>}
          </div>
        </form>
        {(create.error || toggle.error || remove.error) != null && (
          <div className="px-5 pb-4">
            <ErrorNote error={create.error ?? toggle.error ?? remove.error} />
          </div>
        )}
      </Card>

      <Card>
        {tasks.isPending ? (
          <div className="p-5"><Spinner label="Loading scheduled tasks…" /></div>
        ) : tasks.error ? (
          <div className="p-5"><ErrorNote error={tasks.error} /></div>
        ) : tasks.data.length === 0 ? (
          <EmptyState title="No scheduled tasks" description="Add a disabled task above, verify it, then enable it." />
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {tasks.data.map((task) => (
              <div key={task.uuid} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{task.name}</p>
                    <Badge tone={task.enabled ? 'green' : 'gray'}>
                      {task.enabled ? 'enabled' : 'disabled'}
                    </Badge>
                  </div>
                  <p className="break-all font-mono text-xs text-slate-500">{task.command}</p>
                  <p className="text-xs text-slate-500">{task.frequency} · timeout {task.timeout}s</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => { setEditing(task.uuid); setName(task.name); setCommand(task.command); setFrequency(task.frequency); setTimeoutValue(task.timeout); }}>Edit task</Button>
                  <Button variant="secondary" onClick={() => setSelectedTask(task.uuid)}>
                    Executions
                  </Button>
                  <Button
                    variant="secondary"
                    loading={toggle.isPending}
                    onClick={() => toggle.mutate(task)}
                  >
                    {task.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => { if (window.confirm(`Delete scheduled task "${task.name}"?`)) remove.mutate(task.uuid); }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {selectedTask && (
        <Card>
          <CardHeader title="Recent scheduled-task executions" />
          <div className="px-5 py-4">
            {executions.isPending ? (
              <Spinner />
            ) : executions.error ? (
              <ErrorNote error={executions.error} />
            ) : executions.data.length === 0 ? (
              <p className="text-sm text-slate-500">No executions recorded.</p>
            ) : (
              <div className="space-y-2 text-sm">
                {executions.data.map((execution, index) => (
                  <div key={`${execution.id ?? index}`} className="flex justify-between gap-4">
                    <span className="break-all">{customer ? execution.startedAt ?? 'Time unavailable' : execution.message ?? 'No output summary'}</span>
                    <Badge tone={statusTone(execution.status ?? 'unknown')}>
                      {execution.status ?? 'unknown'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
